// apps/client/src/hooks/useBrokerStatuses.ts
// Hook to fetch broker active/inactive status from Firestore

import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface BrokerStatus {
  brokerId: string;
  brokerName: string;
  status: 'active' | 'inactive';
}

interface UseBrokerStatusesReturn {
  brokerStatuses: Map<string, 'active' | 'inactive'>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch broker status from Firestore brokers collection
 * Maps broker display name to their active/inactive status
 */
export function useBrokerStatuses(
  companyId: string,
  officeId?: string
): UseBrokerStatusesReturn {
  const [brokerStatuses, setBrokerStatuses] = useState<Map<string, 'active' | 'inactive'>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchBrokerStatuses = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query - filter by office if specified
        let q = query(collection(db, `companies/${companyId}/brokers`));

        if (officeId && officeId !== 'all') {
          // Use allOfficeIds to find brokers who have ever been in this office
          q = query(
            collection(db, `companies/${companyId}/brokers`),
            where('allOfficeIds', 'array-contains', officeId)
          );
        }

        const snapshot = await getDocs(q);

        const statusMap = new Map<string, 'active' | 'inactive'>();

        snapshot.docs.forEach(doc => {
          const data = doc.data();

          // Skip merged brokers - they should not appear in any status map
          if (data.mergedInto) {
            return;
          }

          const brokerName = data.currentName || data.legalName || 'Unknown';

          // Compute status: respect both the Firestore status field AND office assignment
          let status: 'active' | 'inactive';
          const firestoreStatus: 'active' | 'inactive' = (data.status || 'active') === 'active' ? 'active' : 'inactive';

          if (officeId && officeId !== 'all') {
            // Office dashboard: active = currently in this office AND Firestore status is active
            const isCurrentOffice = data.currentOfficeId === officeId;
            status = (isCurrentOffice && firestoreStatus === 'active') ? 'active' : 'inactive';
          } else {
            // Main dashboard: use the broker's Firestore status field directly
            status = firestoreStatus;
          }

          statusMap.set(brokerName, status);
        });

        setBrokerStatuses(statusMap);
      } catch (err: any) {
        console.error('[useBrokerStatuses] Error fetching broker statuses:', err);
        setError(err.message || 'Failed to fetch broker statuses');
      } finally {
        setLoading(false);
      }
    };

    fetchBrokerStatuses();
  }, [companyId, officeId]);

  return {
    brokerStatuses,
    loading,
    error,
  };
}
